<script setup lang="ts">
    import { inject, onMounted, reactive } from 'vue';
    import { getTopAccounts, type Period, type UnsubscribeReasonAccountStats } from './UnsubscribeReasonStats.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { AccountAccess } from '@account/Account.access';
    import List from '@shared/components/List/List.vue';
    import { type tabs } from '@shared/components/List/ListTypes';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const accountAccess: AccountAccess = injectorAccess.injector.get(AccountAccess);

    const state = reactive<{
        accounts: UnsubscribeReasonAccountStats[];
        isLoading: boolean;
        period: Period;
    }>({
        accounts: [],
        isLoading: false,
        period: 'month',
    });

    const periods: tabs = {
        day: { name: 'day', display: 'Day' },
        week: { name: 'week', display: 'Week' },
        month: { name: 'month', display: 'Month' },
    };

    const orderOptions = [
        { display: 'Unsubscribes', key: 'UnsubscribeCount' },
        { display: 'Of which spam', key: 'SpamCount' },
        { display: 'Account', key: 'AccountName' },
        { display: 'Latest', key: 'LatestUnsubscribe' },
    ];

    async function load(): Promise<void> {
        state.isLoading = true;
        state.accounts = await getTopAccounts(state.period).finally(() => (state.isLoading = false));
    }

    async function setPeriod(period: string): Promise<void> {
        state.period = period as Period;
        await load();
    }

    onMounted(load);
</script>

<template>
    <h1>Unsubscribe Reason Stats</h1>

    <List
        class="th2-text-right th3-text-right th4-text-right"
        display-as="table"
        itemId="AccountId"
        :items="state.accounts"
        :loading="state.isLoading"
        :table-headers="['Account', 'Unsubscribes', 'Of which spam', 'Latest']"
        :order-options="orderOptions"
        :filter-keys="['AccountName']"
        :tabs="periods"
        :current-tab="state.period"
        @update:tab="setPeriod"
        order-by-direction="desc">
        <template #item="{ item, index }">
            <td>
                <a href="javascript:void(0)" @click="accountAccess.switchToAccountById(item.AccountId)" role="button">
                    {{ item.AccountName }}
                </a>
            </td>
            <td class="text-right">
                {{ item.UnsubscribeCount }}
            </td>
            <td class="text-right">
                {{ item.SpamCount }}
            </td>
            <td class="text-right">
                {{ new Date(item.LatestUnsubscribe).toLocaleDateString() }}
            </td>
        </template>
    </List>
</template>
